import * as THREE from 'three';

/**
 * WHERE TO CUT THE SOCKET — six interchangeable strategies.
 *
 * The loft that bridges the socket cut down to the frustum's wide rim is only
 * smooth if, at the cut, the socket cross-section fully CONTAINS the rim:
 *
 *     socketRadius(θ) >= wideRadius     for every θ
 *
 * So the real question is "what is the LOWEST cut whose cross-section clears the
 * rim", which is a containment test — not "where is the highest ray hit", which
 * is what the original probe measured. A ray fired outward at 45° lands on socket
 * material far outside the rim's footprint (material that can never constrain the
 * loft), and if the wall's local slope happens to match the ray it grazes and the
 * hit runs away up the socket. That is the "cut is almost at the top" failure.
 *
 * Every strategy here returns the same thing: a per-column AXIAL HEIGHT FIELD
 * h(θ), measured along `towardSocket` from the frustum's wide-rim centre. A flat
 * cut is just a constant field, so planar and non-planar strategies share one
 * consumer. See {@link computeSocketCut}.
 */

/** Radial breathing room added to wideRadius before a point counts as "clear" (cm). */
const CLEARANCE_CM = 0.05;

/**
 * Original probe: rim rays × outward angle fan. Kept verbatim for comparison.
 * 128 rays is the count the pipeline has always used; the preview now matches it
 * exactly (it used to probe at 96) so previewed and built cuts cannot disagree.
 */
const PROBE_RAYS = 128;
const PROBE_ANGLES_DEG = [0, 5, 10, 15, 20, 30, 45];

/** fanFiltered: reject hits landing beyond this multiple of wideRadius. */
const FOOTPRINT_FACTOR = 1.25;
/** fanFiltered: reject hits whose face normal is within this of perpendicular to the ray. */
const GRAZING_MIN_DOT = Math.cos((75 * Math.PI) / 180);

/** radiusProfile: coarse bracket samples, then bisection steps. */
const PROFILE_BRACKET_SAMPLES = 16;
const PROFILE_BISECT_STEPS = 8;
const PROFILE_DIRS = 48;

/** Harmonics retained by the two smooth-fit strategies. */
const TILTED_HARMONICS = 1; // a plane unrolls to EXACTLY one harmonic
const CURVE_HARMONICS = 6;

export const SOCKET_CUT_STRATEGY = {
  CURRENT: 'current',
  VERTEX_SCAN: 'vertexScan',
  RADIUS_PROFILE: 'radiusProfile',
  FAN_FILTERED: 'fanFiltered',
  TILTED_PLANE: 'tiltedPlane',
  CUT_CURVE: 'cutCurve',
};

/**
 * The strategy the REAL pipeline slices with (bridgeFormMeshToSocketPrintable).
 * The preview tabs are a comparison harness and do NOT drive the build — change
 * this one constant to promote a different strategy.
 */
export const PIPELINE_SOCKET_CUT_STRATEGY = SOCKET_CUT_STRATEGY.FAN_FILTERED;

/** Card-facing metadata — label, one-line description, planar/non-planar. */
export const SOCKET_CUT_STRATEGY_INFO = [
  {
    id: SOCKET_CUT_STRATEGY.CURRENT,
    short: 'Current',
    name: 'Ray fan → global max',
    hint: '96 rim rays × 7 outward angles; the single highest axial hit sets one flat plane. Spikes when an angled ray grazes a wall of similar slope.',
    planar: true,
  },
  {
    id: SOCKET_CUT_STRATEGY.VERTEX_SCAN,
    short: 'Vertex',
    name: 'Radial-clearance vertex scan',
    hint: 'No rays. Cut just above the highest socket vertex sitting inside the rim radius. Nothing to spike — a vertex either intrudes or it does not.',
    planar: true,
  },
  {
    id: SOCKET_CUT_STRATEGY.RADIUS_PROFILE,
    short: 'Profile',
    name: 'Min-radius profile + bisection',
    hint: 'Same test, sampled off the surface: at each candidate height take the MIN hit radius over all directions, then bisect for the lowest passing height.',
    planar: true,
  },
  {
    id: SOCKET_CUT_STRATEGY.FAN_FILTERED,
    short: 'Filtered',
    name: 'Ray fan + outlier rejection',
    hint: 'The original fan with grazing hits and hits outside the rim footprint thrown away. Cheapest fix, but still two thresholds to tune.',
    planar: true,
  },
  {
    id: SOCKET_CUT_STRATEGY.TILTED_PLANE,
    short: 'Tilted',
    name: 'Tilted plane (1 harmonic)',
    hint: 'Still one flat plane, but free to tilt off the adapter axis. Recovers the socket a perpendicular cut wastes on an oblique fitting.',
    planar: true,
  },
  {
    id: SOCKET_CUT_STRATEGY.CUT_CURVE,
    short: 'Curve',
    name: 'Non-planar cut curve',
    hint: 'Each sector cut at its own clearance height, smoothed around the ring. Removes the least socket; needs a non-planar slice to build for real.',
    planar: false,
  },
];

const infoFor = (id) =>
  SOCKET_CUT_STRATEGY_INFO.find((s) => s.id === id) || SOCKET_CUT_STRATEGY_INFO[0];

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

/** Furthest socket point along `towardSocket`, from the wide-rim centre (bbox corners). */
function socketAxialMax(socketMesh, wideCenter, towardSocket) {
  const geo = socketMesh.geometry;
  if (!geo.boundingBox) geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const v = new THREE.Vector3();
  let max = -Infinity;
  for (let i = 0; i < 8; i++) {
    v.set(
      i & 1 ? bb.max.x : bb.min.x,
      i & 2 ? bb.max.y : bb.min.y,
      i & 4 ? bb.max.z : bb.min.z,
    );
    v.applyMatrix4(socketMesh.matrixWorld).sub(wideCenter);
    const d = v.dot(towardSocket);
    if (d > max) max = d;
  }
  return max;
}

/**
 * Per-sector clearance height: for each angular column, the highest socket vertex
 * that still sits INSIDE `wideRadius + CLEARANCE_CM`. Above that height the sector
 * is wide enough for the loft. Sectors with no intruding vertex stay 0.
 *
 * This one pass feeds vertexScan (take the max), tiltedPlane and cutCurve (fit a
 * curve to the field). World-space transform is inlined — no per-vertex allocation.
 *
 * @returns {{ heights: Float64Array, hits: number, aboveRim: number }}
 */
function sectorClearanceHeights(ctx) {
  const { socketMesh, wideCenter, towardSocket, u, w, wideRadius, columns } = ctx;
  const rNeed = wideRadius + CLEARANCE_CM;
  const rNeedSq = rNeed * rNeed;

  const arr = socketMesh.geometry.getAttribute('position').array;
  const e = socketMesh.matrixWorld.elements;
  const cx = wideCenter.x, cy = wideCenter.y, cz = wideCenter.z;
  const tx = towardSocket.x, ty = towardSocket.y, tz = towardSocket.z;
  const ux = u.x, uy = u.y, uz = u.z;
  const wx = w.x, wy = w.y, wz = w.z;

  const heights = new Float64Array(columns);
  const TWO_PI = Math.PI * 2;
  let hits = 0;
  let aboveRim = 0;

  for (let i = 0; i < arr.length; i += 3) {
    const lx = arr[i], ly = arr[i + 1], lz = arr[i + 2];
    const px = e[0] * lx + e[4] * ly + e[8] * lz + e[12];
    const py = e[1] * lx + e[5] * ly + e[9] * lz + e[13];
    const pz = e[2] * lx + e[6] * ly + e[10] * lz + e[14];
    const dx = px - cx, dy = py - cy, dz = pz - cz;

    const a = dx * tx + dy * ty + dz * tz;        // axial height above the rim
    if (a <= 0) continue;                          // below the rim — cannot constrain
    aboveRim++;

    const rx = dx - a * tx, ry = dy - a * ty, rz = dz - a * tz;
    if (rx * rx + ry * ry + rz * rz >= rNeedSq) continue;   // already clears the rim
    hits++;

    let th = Math.atan2(rx * wx + ry * wy + rz * wz, rx * ux + ry * uy + rz * uz);
    if (th < 0) th += TWO_PI;
    const k = Math.min(columns - 1, (th / TWO_PI * columns) | 0);
    if (a > heights[k]) heights[k] = a;
  }
  return { heights, hits, aboveRim };
}

/** Circular max-filter — keeps a lone tall sector from being averaged away. */
function dilateCircular(h, radius) {
  const n = h.length;
  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let m = h[i];
    for (let d = 1; d <= radius; d++) {
      m = Math.max(m, h[(i - d + n) % n], h[(i + d) % n]);
    }
    out[i] = m;
  }
  return out;
}

/**
 * Keep only the first `K` circular harmonics of a height field.
 *
 * K = 1 is not an approximation of a tilted plane — it IS one: a plane cutting a
 * cylinder produces exactly `A + B·cos(θ − φ)` when unrolled. Larger K lets the
 * cut follow the socket's own undulation.
 */
function fourierLowPass(h, K) {
  const n = h.length;
  const out = new Float64Array(n);
  const a = new Float64Array(K + 1);
  const b = new Float64Array(K + 1);
  for (let k = 0; k <= K; k++) {
    let ak = 0;
    let bk = 0;
    for (let i = 0; i < n; i++) {
      const t = (2 * Math.PI * i) / n;
      ak += h[i] * Math.cos(k * t);
      bk += h[i] * Math.sin(k * t);
    }
    a[k] = k === 0 ? ak / n : (2 * ak) / n;
    b[k] = k === 0 ? 0 : (2 * bk) / n;
  }
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    let s = a[0];
    for (let k = 1; k <= K; k++) s += a[k] * Math.cos(k * t) + b[k] * Math.sin(k * t);
    out[i] = s;
  }
  return out;
}

/** Raise `fitted` uniformly until it covers `raw` everywhere (a fit may dip below). */
function liftToCover(fitted, raw) {
  let lift = 0;
  for (let i = 0; i < raw.length; i++) lift = Math.max(lift, raw[i] - fitted[i]);
  if (lift <= 0) return fitted;
  const out = new Float64Array(fitted.length);
  for (let i = 0; i < fitted.length; i++) out[i] = fitted[i] + lift;
  return out;
}

const constantField = (columns, value) => {
  const h = new Float64Array(columns);
  h.fill(value);
  return h;
};

/** Perpendicular in-plane basis (u, w) for a unit axis — matches the pipeline. */
export function axisBasis(axis) {
  const u = new THREE.Vector3();
  if (Math.abs(axis.y) < 0.9) u.set(0, 1, 0).cross(axis).normalize();
  else u.set(1, 0, 0).cross(axis).normalize();
  const w = new THREE.Vector3().crossVectors(axis, u).normalize();
  return { u, w };
}

// ---------------------------------------------------------------------------
// strategies
// ---------------------------------------------------------------------------

/**
 * CURRENT — every rim point fires an axial ray plus an outward fan; the single
 * highest axial contact anywhere sets one flat plane. Reproduced exactly as the
 * pipeline has it so the tab comparison is honest.
 */
function strategyCurrent(ctx, { filtered = false } = {}) {
  const { socketMesh, wideCenter, towardSocket, u, w, wideRadius, columns } = ctx;
  const ray = new THREE.Raycaster();
  ray.near = 1e-4;
  ray.far = 100;

  const normalMat = filtered
    ? new THREE.Matrix3().getNormalMatrix(socketMesh.matrixWorld)
    : null;
  const maxOffset = wideRadius * FOOTPRINT_FACTOR;

  const radialDir = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const rel = new THREE.Vector3();
  const nrm = new THREE.Vector3();

  // Both maxima come out of the same pass: `maxKept` is the filtered answer,
  // `maxAll` the historic unfiltered one. Tracking both costs nothing and lets a
  // filter that rejects EVERYTHING (degenerate socket, adapter barely grazing)
  // fall back to the old behaviour instead of failing the build outright.
  let maxKept = -Infinity;
  let maxAll = -Infinity;
  let rejected = 0;

  for (let i = 0; i < PROBE_RAYS; i++) {
    const ang = (i / PROBE_RAYS) * Math.PI * 2;
    radialDir.copy(u).multiplyScalar(Math.cos(ang)).addScaledVector(w, Math.sin(ang)).normalize();
    const origin = wideCenter.clone()
      .addScaledVector(radialDir, wideRadius)
      .addScaledVector(towardSocket, 1e-3);

    for (const angleDeg of PROBE_ANGLES_DEG) {
      const angleRad = (angleDeg * Math.PI) / 180;
      dir.copy(towardSocket).multiplyScalar(Math.cos(angleRad))
        .addScaledVector(radialDir, Math.sin(angleRad))
        .normalize();
      ray.set(origin, dir);
      const hits = ray.intersectObject(socketMesh);
      if (hits.length === 0) continue;
      const hit = hits[0];

      const axial = hit.point.clone().sub(wideCenter).dot(towardSocket);
      if (axial > maxAll) maxAll = axial;

      if (filtered) {
        // Reject hits outside the rim footprint — material out there is at a
        // radius the loft never has to reach, so it cannot constrain the cut.
        rel.copy(hit.point).sub(wideCenter);
        const axialComp = rel.dot(towardSocket);
        const offset = rel.addScaledVector(towardSocket, -axialComp).length();
        if (offset > maxOffset) { rejected++; continue; }
        // Reject grazing hits — a ray near-parallel to the wall reports a
        // distance that says more about the ray than about the socket.
        if (hit.face?.normal) {
          nrm.copy(hit.face.normal).applyMatrix3(normalMat).normalize();
          if (Math.abs(nrm.dot(dir)) < GRAZING_MIN_DOT) { rejected++; continue; }
        }
      }

      if (axial > maxKept) maxKept = axial;
    }
  }

  if (!filtered) {
    if (!Number.isFinite(maxAll)) return null;
    return {
      heights: constantField(columns, maxAll),
      detail: `${PROBE_RAYS}×${PROBE_ANGLES_DEG.length} rays`,
    };
  }

  if (Number.isFinite(maxKept)) {
    return {
      heights: constantField(columns, maxKept),
      detail: `${rejected} hits rejected`,
    };
  }
  // Every hit was rejected — better to cut like we always did than not at all.
  if (!Number.isFinite(maxAll)) return null;
  console.warn('[SocketCut] fanFiltered rejected every hit; falling back to the unfiltered max');
  return {
    heights: constantField(columns, maxAll),
    detail: `fallback: all ${rejected} hits rejected`,
  };
}

/** OPTION 1 — cut just above the highest vertex intruding into the rim radius. */
function strategyVertexScan(ctx) {
  const { columns } = ctx;
  const { heights, hits, aboveRim } = sectorClearanceHeights(ctx);
  if (aboveRim === 0) return null;             // adapter parked away from the socket
  let max = 0;
  for (let i = 0; i < heights.length; i++) max = Math.max(max, heights[i]);
  return {
    heights: constantField(columns, max + CLEARANCE_CM),
    detail: `${hits} intruding verts`,
  };
}

/**
 * OPTION 2 — at a candidate height, cast outward from the axis in every direction
 * and take the MIN hit radius. `min` is what makes it robust: the worst sector
 * decides, so no single ray can inflate the answer (the current probe takes a max,
 * so any single ray can). Coarse bracket first, then bisect.
 */
function strategyRadiusProfile(ctx) {
  const { socketMesh, wideCenter, towardSocket, u, w, wideRadius, columns } = ctx;
  const rNeed = wideRadius + CLEARANCE_CM;
  const hMax = socketAxialMax(socketMesh, wideCenter, towardSocket);
  if (!Number.isFinite(hMax) || hMax <= 0) return null;

  const ray = new THREE.Raycaster();
  ray.near = 1e-4;
  ray.far = Math.max(hMax * 4, wideRadius * 20);
  const radialDir = new THREE.Vector3();
  const origin = new THREE.Vector3();

  /** true when the whole cross-section at `h` clears the rim */
  const passes = (h) => {
    origin.copy(wideCenter).addScaledVector(towardSocket, h);
    for (let i = 0; i < PROFILE_DIRS; i++) {
      const ang = (i / PROFILE_DIRS) * Math.PI * 2;
      radialDir.copy(u).multiplyScalar(Math.cos(ang)).addScaledVector(w, Math.sin(ang)).normalize();
      ray.set(origin.clone().addScaledVector(radialDir, 1e-4), radialDir);
      const hits = ray.intersectObject(socketMesh);
      // No hit at all = we are outside/below the socket here, not "infinitely wide".
      if (hits.length === 0) return false;
      if (hits[0].distance < rNeed) return false;   // first hit = cavity wall
    }
    return true;
  };

  // Coarse bracket: walk DOWN from the top and stop at the last height that still
  // passes, so a re-entrant bulge above the answer cannot be bisected past.
  const step = hMax / PROFILE_BRACKET_SAMPLES;
  let lo = null;
  let hi = null;
  for (let i = PROFILE_BRACKET_SAMPLES; i >= 0; i--) {
    const h = i * step;
    if (passes(h)) { hi = h; continue; }
    lo = h;
    break;
  }
  if (hi === null) return null;                    // never clears — no valid cut
  if (lo === null) return { heights: constantField(columns, 0), detail: 'clear at the rim' };

  for (let k = 0; k < PROFILE_BISECT_STEPS; k++) {
    const mid = (lo + hi) / 2;
    if (passes(mid)) hi = mid; else lo = mid;
  }
  return {
    heights: constantField(columns, hi),
    detail: `${PROFILE_BRACKET_SAMPLES}+${PROFILE_BISECT_STEPS} probes × ${PROFILE_DIRS} dirs`,
  };
}

/** OPTIONS 4 & 5 — fit a K-harmonic curve to the per-sector clearance field. */
function strategyFittedField(ctx, harmonics) {
  const { aboveRim, heights } = sectorClearanceHeights(ctx);
  if (aboveRim === 0) return null;

  // Dilate first: the fit is evaluated between sample points too, so a lone tall
  // sector needs to influence its neighbours before it gets smoothed.
  const raw = dilateCircular(heights, 1);
  for (let i = 0; i < raw.length; i++) raw[i] += CLEARANCE_CM;
  const fitted = liftToCover(fourierLowPass(raw, harmonics), raw);
  return { heights: fitted, detail: `${harmonics} harmonic${harmonics === 1 ? '' : 's'}` };
}

// ---------------------------------------------------------------------------

/**
 * Resolve a cut for the given strategy.
 *
 * @param {string} strategy - a {@link SOCKET_CUT_STRATEGY} value
 * @param {object} ctx
 * @param {THREE.Mesh}    ctx.socketMesh   - world matrix already updated, BVH built
 * @param {THREE.Vector3} ctx.wideCenter   - frustum wide-rim centre (world)
 * @param {THREE.Vector3} ctx.towardSocket - unit axis, wide rim → socket
 * @param {THREE.Vector3} ctx.u
 * @param {THREE.Vector3} ctx.w            - in-plane basis from {@link axisBasis}
 * @param {number}        ctx.wideRadius
 * @param {number}        ctx.columns      - height-field resolution
 * @returns {{
 *   heights: Float64Array, strategy: string, label: string, planar: boolean,
 *   minH: number, maxH: number, ms: number, detail: string,
 * } | null} null when the socket is out of reach entirely.
 */
export function computeSocketCut(strategy, ctx) {
  const id = SOCKET_CUT_STRATEGY_INFO.some((s) => s.id === strategy)
    ? strategy
    : SOCKET_CUT_STRATEGY.CURRENT;
  const t0 = performance.now();

  let res = null;
  switch (id) {
    case SOCKET_CUT_STRATEGY.VERTEX_SCAN:    res = strategyVertexScan(ctx); break;
    case SOCKET_CUT_STRATEGY.RADIUS_PROFILE: res = strategyRadiusProfile(ctx); break;
    case SOCKET_CUT_STRATEGY.FAN_FILTERED:   res = strategyCurrent(ctx, { filtered: true }); break;
    case SOCKET_CUT_STRATEGY.TILTED_PLANE:   res = strategyFittedField(ctx, TILTED_HARMONICS); break;
    case SOCKET_CUT_STRATEGY.CUT_CURVE:      res = strategyFittedField(ctx, CURVE_HARMONICS); break;
    default:                                 res = strategyCurrent(ctx); break;
  }
  if (!res) return null;

  let minH = Infinity;
  let maxH = -Infinity;
  for (let i = 0; i < res.heights.length; i++) {
    if (res.heights[i] < minH) minH = res.heights[i];
    if (res.heights[i] > maxH) maxH = res.heights[i];
  }

  const info = infoFor(id);
  return {
    heights: res.heights,
    strategy: id,
    label: info.name,
    planar: info.planar,
    detail: res.detail,
    minH,
    maxH,
    ms: performance.now() - t0,
  };
}
